import axios, { AxiosInstance, AxiosError } from 'axios';
import type { ApiError } from '../types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: `${API_URL}/api`,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 15000,
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor - Add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - Handle errors and token refresh
    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<ApiError>) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && originalRequest) {
          const refreshToken = localStorage.getItem('refresh_token');

          if (refreshToken) {
            try {
              const response = await this.refreshToken(refreshToken);
              localStorage.setItem('access_token', response.access_token);
              if (response.refresh_token) {
                localStorage.setItem('refresh_token', response.refresh_token);
              }

              // Retry original request
              originalRequest.headers.Authorization = `Bearer ${response.access_token}`;
              return this.api.request(originalRequest);
            } catch {
              // Refresh failed, logout user
              this.clearTokens();
              window.location.href = '/login';
            }
          } else {
            this.clearTokens();
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  private clearTokens() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }

  private async refreshToken(refreshToken: string) {
    const response = await axios.post(`${API_URL}/api/auth/refresh`, {
      refresh_token: refreshToken,
    });
    return response.data;
  }

  // Auth endpoints
  async login(email: string, password: string) {
    const response = await this.api.post('/auth/login', { email, password });
    return response.data;
  }

  async register(email: string, password: string, name: string) {
    const response = await this.api.post('/auth/register', { email, password, name });
    return response.data;
  }

  async logout() {
    try {
      await this.api.post('/auth/logout');
    } finally {
      this.clearTokens();
    }
  }

  async getMe() {
    const response = await this.api.get('/auth/me');
    return response.data;
  }

  async updateProfile(data: { name: string }) {
    const response = await this.api.patch('/auth/me', data);
    return response.data;
  }

  async changePassword(currentPassword: string, newPassword: string) {
    const response = await this.api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  }

  // Zoom endpoints
  async getZoomAuthUrl() {
    const response = await this.api.get('/zoom/authorize');
    return response.data;
  }

  async getZoomAccounts() {
    const response = await this.api.get('/zoom/accounts');
    return response.data;
  }

  async disconnectZoomAccount(accountId: string) {
    const response = await this.api.delete(`/zoom/accounts/${accountId}`);
    return response.data;
  }

  async getZoomSignature(meetingNumber: string, role: number = 0) {
    const response = await this.api.post('/zoom/signature', {
      meeting_number: meetingNumber,
      role,
    });
    return response.data;
  }

  // Meeting endpoints
  async createMeeting(data: {
    zoom_account_id: string;
    topic: string;
    description?: string;
    start_time: string;
    duration: number;
    timezone?: string;
    meeting_type?: string;
    settings?: Record<string, any>;
  }) {
    const response = await this.api.post('/meetings', data);
    return response.data;
  }

  async getMeetings(params?: { status?: string; limit?: number; offset?: number }) {
    const response = await this.api.get('/meetings', { params });
    return response.data;
  }

  async getMeeting(id: string) {
    const response = await this.api.get(`/meetings/${id}`);
    return response.data;
  }

  async updateMeeting(id: string, data: Record<string, any>) {
    const response = await this.api.patch(`/meetings/${id}`, data);
    return response.data;
  }

  async deleteMeeting(id: string) {
    const response = await this.api.delete(`/meetings/${id}`);
    return response.data;
  }

  async importMeetings(zoomAccountId: string) {
    const response = await this.api.post('/meetings/import', {
      zoom_account_id: zoomAccountId,
    });
    return response.data;
  }

  // Embed endpoints
  async createEmbed(data: {
    meeting_id: string;
    customization?: Record<string, any>;
    allowed_domains?: string[];
  }) {
    const response = await this.api.post('/embeds', data);
    return response.data;
  }

  async getEmbeds(params?: { limit?: number; offset?: number }) {
    const response = await this.api.get('/embeds', { params });
    return response.data;
  }

  async getEmbed(id: string) {
    const response = await this.api.get(`/embeds/${id}`);
    return response.data;
  }

  async updateEmbed(id: string, data: Record<string, any>) {
    const response = await this.api.patch(`/embeds/${id}`, data);
    return response.data;
  }

  async deleteEmbed(id: string) {
    const response = await this.api.delete(`/embeds/${id}`);
    return response.data;
  }

  // Analytics endpoints
  async getAnalyticsOverview(days: number = 30) {
    const response = await this.api.get('/analytics/overview', { params: { days } });
    return response.data;
  }

  async getEmbedAnalytics(embedId: string, days: number = 30) {
    const response = await this.api.get(`/analytics/embeds/${embedId}`, { params: { days } });
    return response.data;
  }

  async getMeetingAnalytics(meetingId: string, days: number = 30) {
    const response = await this.api.get(`/analytics/meetings/${meetingId}`, { params: { days } });
    return response.data;
  }
}

export default new ApiService();
